<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="translate-y-2 opacity-0 motion-reduce:translate-y-0"
      leave-active-class="transition duration-200 ease-out"
      leave-to-class="translate-y-2 opacity-0 motion-reduce:translate-y-0"
    >
      <!-- pointer-events-none on the rail so a bar pinned across the viewport
           can't swallow clicks on the palette behind it.

           z-[45] is a deliberate slot in the homepage's stacking budget, not a
           round number: the results column is `lg:fixed lg:inset-0 lg:z-40`
           (pages/index.vue), so at a plain z-40 this bar ties with it and the
           column's right border and logo paint straight through the panel. The
           ceiling is just as real — the modals must stay on top of a prompt
           that never blocks the page: BaseModal's dialog is z-50 and
           AboutProjectModal is z-[70]. Anything from 41 to 49 works; this sits
           in the middle of that gap. -->
      <div
        v-if="state !== 'hidden'"
        class="pointer-events-none fixed inset-x-0 bottom-0 z-[45] flex justify-center px-4 pb-4"
      >
        <div
          class="pointer-events-auto w-full max-w-xl rounded-xl border border-white/10 bg-gray-900/95 shadow-xl ring-1 ring-black/20 backdrop-blur"
          role="region"
          aria-label="Rate this palette"
        >
          <!-- ── Step 1: yes / no ─────────────────────────────────────── -->
          <div v-if="state === 'ask'" class="flex items-center gap-3 px-4 py-3">
            <p class="min-w-0 flex-1 text-sm text-white">
              <span class="sm:hidden">Happy with this palette?</span>
              <span class="hidden sm:inline">Are you happy with this palette?</span>
            </p>

            <BaseButton variant="soft" size="xs" class="shrink-0" :disabled="submitting" @click="rateYes">
              <template #icon>
                <HandThumbUpIcon class="size-4" aria-hidden="true" />
              </template>
              Yes
            </BaseButton>

            <BaseButton variant="soft" size="xs" class="shrink-0" :disabled="submitting" @click="rateNo">
              <template #icon>
                <HandThumbDownIcon class="size-4" aria-hidden="true" />
              </template>
              No
            </BaseButton>

            <CloseButton label="Dismiss feedback prompt" size="sm" @click="dismiss" />
          </div>

          <!-- ── Step 2: what went wrong ──────────────────────────────── -->
          <div v-else-if="state === 'why'" class="space-y-3 px-4 py-3">
            <div class="flex items-center gap-3">
              <p class="min-w-0 flex-1 text-sm text-white">What went wrong?</p>
              <CloseButton label="Dismiss feedback prompt" size="sm" @click="dismiss" />
            </div>

            <div class="flex flex-wrap gap-1.5">
              <button
                v-for="option in FEEDBACK_REASONS"
                :key="option.slug"
                type="button"
                class="rounded-md px-2.5 py-1 text-xs font-medium ring-1 ring-inset transition"
                :class="reason === option.slug
                  ? 'bg-indigo-500 text-white ring-indigo-400'
                  : 'bg-white/10 text-gray-300 ring-white/10 hover:bg-white/20'"
                :aria-pressed="reason === option.slug"
                @click="selectReason(option.slug)"
              >{{ option.label }}</button>
            </div>

            <div class="flex items-center gap-2">
              <TextInput
                v-model="note"
                type="text"
                :maxlength="FEEDBACK_NOTE_MAX"
                placeholder="Tell us more (optional)"
                aria-label="Additional feedback"
                size="sm"
                class="min-w-0 flex-1"
                @keydown.enter="submitDetail"
              />
              <BaseButton size="sm" class="shrink-0" :disabled="submitting" @click="submitDetail">
                <SpinnerIcon v-if="submitting" class="size-4" />
                {{ reason || note.trim() ? 'Send' : 'Skip' }}
              </BaseButton>
            </div>
          </div>

          <!-- ── Step 3: acknowledgement ──────────────────────────────── -->
          <div v-else class="flex items-center gap-3 px-4 py-3" aria-live="polite">
            <CheckIcon class="size-5 shrink-0 text-indigo-400" aria-hidden="true" />
            <p class="min-w-0 flex-1 text-sm text-white">Thanks, noted.</p>
          </div>

          <!-- Outside the steps: a failed thumbs-up leaves you on step 1, and
               that failure has to be visible there too. -->
          <p v-if="error" class="px-4 pb-3 text-xs text-red-400" role="alert">
            {{ error }}
          </p>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { watch, onMounted, onUnmounted } from 'vue'
import { HandThumbDownIcon, HandThumbUpIcon } from '@heroicons/vue/16/solid'
import { CheckIcon } from '@heroicons/vue/20/solid'
import { FEEDBACK_REASONS, FEEDBACK_NOTE_MAX } from '~/utils/feedback'
import { useResultFeedback } from '~/composables/useResultFeedback'
import BaseButton from '~/components/atoms/BaseButton.vue'
import CloseButton from '~/components/atoms/CloseButton.vue'
import SpinnerIcon from '~/components/atoms/SpinnerIcon.vue'
import TextInput from '~/components/atoms/TextInput.vue'

const props = defineProps({
  /**
   * What's on screen to rate: the url that was fetched and the trace id of the
   * call that answered. Null while generating, on an error, or before anything
   * has been generated — the bar takes itself down in all three cases.
   */
  target: { type: Object, default: null },
})

const {
  state, reason, note, submitting, error,
  prompt, rateYes, rateNo, selectReason, submitDetail, dismiss,
} = useResultFeedback()

watch(() => props.target, t => prompt(t), { immediate: true, deep: true })

// Escape dismisses. Not a focus trap — this prompt never blocks the page, and
// stealing focus from the palette someone is reading would be worse than the
// prompt itself. The guard keeps Escape from closing the bar out from under a
// modal that is on top of it and owns the keystroke (the download and about
// dialogs both render as role="dialog").
function onKeydown(e) {
  if (e.key !== 'Escape' || state.value === 'hidden') return
  if (document.querySelector('[role="dialog"]')) return
  dismiss()
}

onMounted(() => document.addEventListener('keydown', onKeydown))
onUnmounted(() => document.removeEventListener('keydown', onKeydown))
</script>
